import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { useLocation } from 'react-router'
import {
  useLocale,
  useLocaleActions,
} from '@merchant-portal/merchant-portal-base'
import debounce from 'lodash.debounce'
import { midas } from '@resurs/midas'
import TagManager from 'react-gtm-module'
import queryString from 'query-string'

import { Toast } from '@resurs/midas-react'
/* pages */
import LoginRoutes from './pages/Login'
import OngoingPaymentsRoutes from './pages/OngoingPayments'
import PayApplyRoutes from './pages/PayApply'
import ProfileRoutes from './pages/Profile'
import TransactionsRoutes from './pages/Transactions'
import SupportRoutes from './pages/Support'
import StatisticsRoutes from './pages/Statistics'
import HomeRoutes from './pages/Home'
import NewsRoutes from './pages/News'
import StoreSelectRoutes from './pages/StoreSelect'
import UsersRoutes from './pages/Users'
import PasswordRoutes from './pages/Password'
import SettingsRoutes from './pages/Settings'
import BonusChecksRoutes from './pages/BonusChecks'
import ReconciliationRoutes from './pages/Reconciliation'
/* internal components */
import SideNav from './internal/Navigation/SideNav'
import Header from './internal/Navigation/Header'
import VersionNotification from './internal/VersionNotification'

import { AuthContextProvider } from './context/AuthContext'
import useGoogleAnalytics from './util/ga-hooks'
import { Languages } from './models/languages'
import ArticleNotification from './internal/ArticleNotification/ArticleNotification'
import ConfirmEmailChangeRoute from './pages/ConfirmEmailChange'
import { ProtectedRoute } from './containers/Routing'
import { PaginationProvider } from './provider/PaginationProvider'
import PaymentManagementRoutes from './pages/PaymentManagement'
import ApmRouteTracker from './apm/ApmRouteTracker'

const tagManagerArgs = {
  gtmId: 'GTM-P5987RK',
  dataLayer: {
    userId: '001',
    userProject: 'Merchant Portal',
  },
  events: {
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  },
}

TagManager.initialize(tagManagerArgs)

const App = props => {
  const [locale] = useLocale()
  const localeActions = useLocaleActions()
  const { search: queries } = useLocation()
  useEffect(() => {
    const { lang } = queryString.parse(queries)
    if (
      lang &&
      locale.language !== lang &&
      Object.values(Languages).includes(lang)
    ) {
      localeActions.setLanguage(lang)
    }
  }, [queries])
  const [menuOpen, setMenuOpen] = useState(true)

  const updateRoute = newRoute => {
    props.history.push(`/${newRoute}`)
  }

  const nextRoute = nxtRoute => {
    props.history.push(`${props.history.location.pathname}/${nxtRoute}`)
  }

  const checkWidth = () => {
    const debounceResize = debounce(() => {
      if (window.innerWidth < 1281) {
        setMenuOpen(false)
      } else {
        setMenuOpen(true)
      }
    }, 1)
    window.addEventListener('load', debounceResize)
    window.addEventListener('resize', debounceResize)
  }

  useGoogleAnalytics()

  useEffect(() => {
    checkWidth()
  }, [])

  useEffect(() => {
    const { currentRepresentative } = props.storeInfo
    const { country } = locale

    if (currentRepresentative && currentRepresentative.country !== country) {
      localeActions.setCountry(currentRepresentative.country)
    }
  }, [props.storeInfo.currentRepresentative])

  const { representativeId } = props.storeInfo?.currentStore || {}
  return !props.initiated ? null : (
    <main
      style={{ background: '#fafafa' }}
      className={
        props.loggedIn &&
        !props.incompleteUser &&
        props.history.location.pathname !== '/store/select'
          ? menuOpen
            ? midas.sidebarOpen
            : midas.sidebarClosed
          : null
      }
    >
      <VersionNotification />
      <Toast toastContainerStyles={{ marginTop: '69px' }} />

      <ApmRouteTracker />

      <div>
        {props.loggedIn &&
          !props.incompleteUser &&
          !props.selectStore && [
            <SideNav
              key="side-nav"
              isMenuOpen={menuOpen}
              onMenuOpen={setMenuOpen}
            />,
            <Header
              menuOpen={menuOpen}
              isLoggedIn={props.loggedIn && !props.selectStore}
              key="header-container"
            />,
          ]}

        <div
          style={{
            paddingTop: props.loggedIn ? (menuOpen ? '69px' : '39px') : null,
          }}
        >
          {props.loggedIn && !props.selectStore && (
            <ArticleNotification top={!menuOpen ? 10 : 0} />
          )}
          <AuthContextProvider>
            <PaginationProvider>
              <HomeRoutes />
              <LoginRoutes
                role={props.role}
                loggedIn={props.loggedIn}
                selectStore={props.selectStore}
                temporaryPassword={props.temporaryPassword}
                token={props.token}
                incompleteUser={props.incompleteUser}
              />
              <UsersRoutes />
              <SupportRoutes />
              <OngoingPaymentsRoutes
                nextRoute={nextRoute}
                updateRoute={updateRoute}
              />
              <PayApplyRoutes />
              <PasswordRoutes />
              <ProfileRoutes />
              <NewsRoutes />
              <StoreSelectRoutes
                loggedIn={props.loggedIn}
                selectStore={props.selectStore}
              />
              <SettingsRoutes />
              <StatisticsRoutes />
              <TransactionsRoutes
                nextRoute={nextRoute}
                updateRoute={updateRoute}
              />
              <BonusChecksRoutes />
              <ReconciliationRoutes authorities={props.authorities} />
              <ConfirmEmailChangeRoute />
              <PaymentManagementRoutes
                authorities={props.authorities}
                representativeId={representativeId}
              />
              <ProtectedRoute path="*" authorities={props.authorities} />
            </PaginationProvider>
          </AuthContextProvider>
        </div>
      </div>
    </main>
  )
}

App.contextTypes = {
  store: PropTypes.object,
}

App.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  role: PropTypes.string.isRequired,
  selectStore: PropTypes.bool.isRequired,
  initiated: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  temporaryPassword: PropTypes.bool.isRequired,
  incompleteUser: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({
  loggedIn: state.auth.isLoggedIn,
  role: state.auth.role,
  language: state.auth.language,
  selectStore: state.auth.selectStore,
  initiated: state.rehydrate.initiated,
  temporaryPassword: state.auth.temporaryPassword,
  incompleteUser: state.auth.incompleteUser,
  token: state.auth.token,
  appInfo: state.commons.appInfo,
  storeInfo: state.storeInfo,
  authorities: state.auth.authorities,
})

export default withRouter(connect(mapStateToProps, null)(App))
